import { Box, Theme, SxProps, BoxProps, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import { forwardRef } from "react";
import { Button, ButtonProps } from "../Button";
import {
  COLOR_CONSTANTS,
  SPACING_CONSTANTS,
  FONT_SIZES,
} from "../../constants/styles";

export type ModalBottomIntent =
  | "small"
  | "medium"
  | "large"
  | "stepper"
  | "page";

/**
 * 모달 푸터(액션 영역) props — `intent` 별로 레이아웃이 다르다.
 *
 * `small`/`medium`/`large` 는 일반 모달용, `stepper`/`page` 는 다단계 마법사용 (좌우 분산 정렬).
 */
export interface ModalBottomProps extends BoxProps {
  intent?: ModalBottomIntent;
  /** 좌측 버튼 노출 여부 */
  leftButton?: boolean;
  leftButtonText?: string;
  leftButtonProps?: Omit<ButtonProps, "children" | "onClick">;
  onLeftButtonClick?: () => void;
  rightButtonText?: string;
  rightButtonProps?: Omit<ButtonProps, "children" | "onClick">;
  onRightButtonClick?: () => void;
  /** 좌측 텍스트 버튼 슬롯 (예: "건너뛰기") */
  textButton?: string;
  sx?: SxProps<Theme>;
}

export const ModalBottom = forwardRef<HTMLDivElement, ModalBottomProps>(
  (
    {
      sx,
      intent = "medium",
      leftButton = true,
      leftButtonText = "좌버튼",
      leftButtonProps,
      onLeftButtonClick,
      rightButtonText = "우버튼",
      rightButtonProps,
      onRightButtonClick,
      textButton,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();

    const handleLeftButton = () => {
      if (onLeftButtonClick) {
        onLeftButtonClick();
      }
    };

    const handleRightButton = () => {
      if (onRightButtonClick) {
        onRightButtonClick();
      }
    };

    const getButtonSize = () => {
      switch (intent) {
        case "small":
          return "small";
        case "medium":
        case "large":
        case "stepper":
        case "page":
        default:
          return undefined;
      }
    };

    return (
      <Box ref={ref} sx={{ ...container[intent], ...sx }} {...props}>
        {leftButton && (
          <>
            {textButton ? (
              <TextButton
                text={textButton}
                onClick={handleLeftButton}
                theme={theme}
              />
            ) : (
              <Button
                size={getButtonSize()}
                emphasis="outlined"
                {...leftButtonProps}
                onClick={handleLeftButton}
              >
                {leftButtonText}
              </Button>
            )}
          </>
        )}

        <Button
          size={getButtonSize()}
          {...rightButtonProps}
          onClick={handleRightButton}
        >
          {rightButtonText}
        </Button>
      </Box>
    );
  }
);

ModalBottom.displayName = "ModalBottom";

const TextButton = ({
  text,
  onClick,
  theme,
}: {
  text: string;
  onClick?: () => void;
  theme: any;
}) => (
  <Box
    sx={{
      ...textButtonStyle,
      color: theme.palette.text.secondary,
      "&:hover": {
        color: grey[800],
      },
      "&:active": {
        color: grey[900],
      },
    }}
    onClick={onClick}
  >
    {text}
  </Box>
);

const baseContainer = {
  width: "100%",
  display: "flex",
  alignItems: "center",
} as const;

const standardPadding = {
  padding: `${SPACING_CONSTANTS.spacing[4]}px`,
  paddingBottom: `${SPACING_CONSTANTS.spacing[6]}px`,
} as const;

const container = {
  small: {
    ...baseContainer,
    ...standardPadding,
    justifyContent: "space-between",
    borderTop: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
  },
  medium: {
    ...baseContainer,
    ...standardPadding,
    justifyContent: "flex-end",
    gap: 1,
  },
  large: {
    ...baseContainer,
    padding: `${SPACING_CONSTANTS.spacing[4]}px ${SPACING_CONSTANTS.spacing[12]}px ${SPACING_CONSTANTS.spacing[8]}px`,
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 1,
  },
  stepper: {
    ...baseContainer,
    ...standardPadding,
    justifyContent: "space-between",
    gap: 1,
  },
  page: {
    ...baseContainer,
    ...standardPadding,
    justifyContent: "space-between",
  },
} as const;

const textButtonStyle = {
  paddingRight: `${
    SPACING_CONSTANTS.spacing[2] + SPACING_CONSTANTS.spacing[1] / 2
  }px`,
  fontSize: FONT_SIZES.sm,
  fontWeight: 500,
  lineHeight: "24px",
  letterSpacing: "0.4px",
  cursor: "pointer",
  textDecoration: "underline",
  userSelect: "none",
} as const;
